import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Warning as WarningIcon,
  DirectionsCar as CarIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import {
  getAbandonedVehiclesNearby,
  markAbandonedVehicleStillThere,
  markAbandonedVehicleUtl,
} from "../api/abandonedVehicles";

// Distance threshold in meters (0.25 miles)
const PROXIMITY_THRESHOLD_METERS = 402.336;
// How often to check for nearby vehicles (in ms)
const CHECK_INTERVAL_MS = 30000; // 30 seconds
// Minimum distance change before rechecking (in meters)
const MIN_DISTANCE_CHANGE = 50;
// Cooldown before showing same vehicle again (in ms) - 10 minutes
const VEHICLE_COOLDOWN_MS = 600000;

/**
 * Component that monitors GPS location and shows alerts when approaching abandoned vehicles
 */
export default function AbandonedVehicleAlert({ gpsLocation, enabled = true }) {
  const [alertVehicle, setAlertVehicle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Track vehicles that have been shown to user recently (by ID)
  const shownVehiclesRef = useRef(new Map());
  // Track last check location
  const lastCheckLocationRef = useRef(null);
  // Track last check time
  const lastCheckTimeRef = useRef(0);

  const checkForNearbyVehicles = useCallback(async () => {
    console.log("[AbandonedVehicleAlert] Checking...", { enabled, gpsLocation });
    
    if (!enabled || !gpsLocation?.latitude || !gpsLocation?.longitude) {
      console.log("[AbandonedVehicleAlert] Skipping check - missing requirements");
      return;
    }

    const now = Date.now();
    const lastCheck = lastCheckTimeRef.current;
    const lastLocation = lastCheckLocationRef.current;

    // Throttle checks - at least CHECK_INTERVAL_MS between checks
    if (now - lastCheck < CHECK_INTERVAL_MS) {
      // Unless we've moved significantly
      if (lastLocation) {
        const distance = calculateDistance(
          gpsLocation.latitude,
          gpsLocation.longitude,
          lastLocation.latitude,
          lastLocation.longitude
        );
        if (distance < MIN_DISTANCE_CHANGE) {
          console.log("[AbandonedVehicleAlert] Skipping - throttled and not moved enough");
          return;
        }
      } else {
        return;
      }
    }

    lastCheckTimeRef.current = now;
    lastCheckLocationRef.current = {
      latitude: gpsLocation.latitude,
      longitude: gpsLocation.longitude,
    };

    try {
      console.log("[AbandonedVehicleAlert] Fetching nearby vehicles at", gpsLocation.latitude, gpsLocation.longitude);
      const nearbyVehicles = await getAbandonedVehiclesNearby(
        gpsLocation.latitude,
        gpsLocation.longitude,
        PROXIMITY_THRESHOLD_METERS
      );

      console.log("[AbandonedVehicleAlert] Found nearby vehicles:", nearbyVehicles);

      if (nearbyVehicles && nearbyVehicles.length > 0) {
        // Find the closest vehicle that hasn't been shown recently
        for (const vehicle of nearbyVehicles) {
          const lastShown = shownVehiclesRef.current.get(vehicle.id);
          if (!lastShown || now - lastShown > VEHICLE_COOLDOWN_MS) {
            // Show this vehicle
            setAlertVehicle(vehicle);
            shownVehiclesRef.current.set(vehicle.id, now);
            break;
          }
        }
      }
    } catch (err) {
      console.error("Error checking for nearby abandoned vehicles:", err);
      // Don't show error to user for background checks
    }
  }, [enabled, gpsLocation]);

  // Check for nearby vehicles when GPS updates
  useEffect(() => {
    checkForNearbyVehicles();
  }, [checkForNearbyVehicles]);

  // Also set up interval check
  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(checkForNearbyVehicles, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, checkForNearbyVehicles]);

  const handleStillThere = async () => {
    if (!alertVehicle) return;

    setSubmitting(true);
    setError(null);

    try {
      await markAbandonedVehicleStillThere(alertVehicle.id);
      setAlertVehicle(null);
    } catch (err) {
      console.error("Error marking vehicle as still there:", err);
      setError("Failed to update vehicle status");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUtl = async () => {
    if (!alertVehicle) return;

    setSubmitting(true);
    setError(null);

    try {
      await markAbandonedVehicleUtl(alertVehicle.id);
      setAlertVehicle(null);
    } catch (err) {
      console.error("Error marking vehicle as UTL:", err);
      setError("Failed to update vehicle status");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Just close without action - vehicle stays in database
    setAlertVehicle(null);
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "Unknown";
    
    // Ensure the date is parsed as UTC - append 'Z' if not present
    let utcDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      utcDateString = dateString + 'Z';
    }
    
    const date = new Date(utcDateString);
    const now = new Date();
    const diffMs = now - date;
    
    // Handle edge case where time is in the future (clock skew)
    if (diffMs < 0) {
      return "Just now";
    }
    
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
    } else {
      return "Just now";
    }
  };

  const formatDistance = (meters) => {
    if (meters < 1609.34) {
      // Less than 1 mile, show in feet
      const feet = Math.round(meters * 3.28084);
      return `${feet} ft`;
    }
    const miles = (meters / 1609.34).toFixed(2);
    return `${miles} mi`;
  };

  if (!alertVehicle) return null;

  return (
    <Dialog
      open={!!alertVehicle}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderTop: "4px solid #ff9800",
        },
      }}
    >
      <DialogTitle sx={{ textTransform: "uppercase", fontWeight: 700 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningIcon color="warning" />
          <span>Abandoned Vehicle Ahead</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2, textTransform: "uppercase" }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          {/* Distance indicator */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <Chip
              icon={<LocationIcon />}
              label={formatDistance(alertVehicle.distanceMeters)}
              color="warning"
              sx={{ fontSize: "1.1rem", py: 2, px: 1, textTransform: "uppercase" }}
            />
          </Box>

          {/* Vehicle details */}
          <Box
            sx={{
              bgcolor: "background.default",
              p: 2,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <CarIcon color="primary" />
              <Typography variant="h6" sx={{ textTransform: "uppercase", fontWeight: 700 }}>
                {alertVehicle.vehicleYear && `${alertVehicle.vehicleYear} `}{alertVehicle.vehicleColor} {alertVehicle.vehicleMake} {alertVehicle.vehicleModel}
              </Typography>
            </Stack>

            {alertVehicle.vehiclePlate && (
              <Typography variant="body1" sx={{ textTransform: "uppercase", mb: 1 }}>
                <strong>Plate:</strong> {alertVehicle.vehicleState && `${alertVehicle.vehicleState} `}{alertVehicle.vehiclePlate}
              </Typography>
            )}

            {alertVehicle.shoulderLocation && (
              <Typography variant="body1" sx={{ textTransform: "uppercase", mb: 1 }}>
                <strong>Location:</strong> {alertVehicle.shoulderLocation}
              </Typography>
            )}

            {alertVehicle.locationDescription && (
              <Typography variant="body2" sx={{ textTransform: "uppercase", mb: 1 }}>
                {alertVehicle.locationDescription}
              </Typography>
            )}
          </Box>

          {/* Reported info */}
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            {alertVehicle.reportedByName && (
              <Chip
                label={`Reported by ${alertVehicle.reportedByName}`}
                size="small"
                color="primary"
                sx={{ textTransform: "uppercase" }}
              />
            )}
            <Chip
              icon={<TimeIcon />}
              label={`${formatTimeAgo(alertVehicle.reportedAtUtc)}`}
              size="small"
              variant="outlined"
              sx={{ textTransform: "uppercase" }}
            />
            {alertVehicle.confirmationCount > 0 && (
              <Chip
                label={`Confirmed ${alertVehicle.confirmationCount}x`}
                size="small"
                color="info"
                sx={{ textTransform: "uppercase" }}
              />
            )}
          </Stack>

          {alertVehicle.notes && (
            <Typography
              variant="body2"
              sx={{
                textTransform: "uppercase",
                bgcolor: "action.hover",
                color: "text.primary",
                p: 1,
                borderRadius: 1,
                fontStyle: "italic",
              }}
            >
              {alertVehicle.notes}
            </Typography>
          )}

          {/* Instructions */}
          <Alert severity="info" sx={{ textTransform: "uppercase" }}>
            Is this vehicle still at this location?
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", gap: 2, p: 2 }}>
        <Button
          variant="contained"
          color="success"
          onClick={handleStillThere}
          disabled={submitting}
          sx={{ textTransform: "uppercase", minWidth: 150, py: 1.5 }}
        >
          {submitting ? <CircularProgress size={24} /> : "Still There"}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleUtl}
          disabled={submitting}
          sx={{ textTransform: "uppercase", minWidth: 150, py: 1.5 }}
        >
          {submitting ? <CircularProgress size={24} /> : "UTL"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Helper function to calculate distance in meters using Haversine formula
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}
